/**
 * 6-cell OTP input used by every verification card.
 *
 * Mockup: 6 cells, 44x44, gap 8px, amber focus glow.
 * Behaviour:
 *   - Auto-advance on input
 *   - Backspace on empty moves focus left and clears previous
 *   - ArrowLeft/Right move focus
 *   - Paste of a 6-digit string fills all cells then fires onComplete
 *   - On submit (Enter) calls onComplete with the joined value
 */

"use client";

import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

const LEN = 6;

type Props = {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  isError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
};

export function OtpInput({
  value,
  onChange,
  onComplete,
  isError,
  disabled,
  autoFocus = true,
}: Props) {
  const refs = useRef<Array<HTMLInputElement | null>>(Array(LEN).fill(null));
  const [focused, setFocused] = useState(-1);

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  function setAt(i: number, ch: string) {
    const arr = value.padEnd(LEN, " ").split("").slice(0, LEN);
    arr[i] = ch;
    const next = arr.join("").replace(/\s+$/g, "");
    onChange(next);
    if (next.length === LEN && onComplete) onComplete(next);
  }

  function onCellChange(i: number, raw: string) {
    const ch = raw.replace(/\D/g, "").slice(-1);
    if (!ch) {
      setAt(i, "");
      return;
    }
    setAt(i, ch);
    if (i < LEN - 1) refs.current[i + 1]?.focus();
  }

  function onCellKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (!value[i] && i > 0) {
        e.preventDefault();
        setAt(i - 1, "");
        refs.current[i - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < LEN - 1) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
    } else if (e.key === "Enter") {
      if (value.length === LEN && onComplete) onComplete(value);
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (text.length === 0) return;
    e.preventDefault();
    const next = text.slice(0, LEN);
    onChange(next);
    const focusIdx = Math.min(next.length, LEN - 1);
    refs.current[focusIdx]?.focus();
    if (next.length === LEN && onComplete) onComplete(next);
  }

  return (
    <div className="mio-auth-otp">
      {Array.from({ length: LEN }).map((_, i) => {
        const ch = value[i] ?? "";
        return (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoComplete={i === 0 ? "one-time-code" : "off"}
            className={`mio-auth-otp-cell${isError ? " is-error" : ""}`}
            value={ch}
            disabled={disabled}
            onChange={(e) => onCellChange(i, e.target.value)}
            onKeyDown={(e) => onCellKeyDown(i, e)}
            onPaste={onPaste}
            onFocus={() => setFocused(i)}
            onBlur={() => setFocused(-1)}
            aria-label={`Digit ${i + 1}`}
          />
        );
      })}
    </div>
  );
}
